import { type ApiSchemas, call } from '@rental/contracts/client'
import { customerName } from '@rental/domain'
import { format, formatDate, plural } from '@rental/i18n'
import { Button } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api, load } from '../api'
import { Fetching, ROW } from '../components/Results'
import { SearchField } from '../components/SearchForm'
import { StatusChip } from '../components/StatusChip'
import { ProblemNotice } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'

type CustomerRow = ApiSchemas['CustomerRow']

/** A customer from the register: who, and whether the shop can reach them (a phone or an e-mail). */
export interface Chosen {
  id: string
  name: string
  reachable: boolean
}

/**
 * A customer from the register, found by name, e-mail or phone (two letters at
 * least: the server's own rule). Choosing takes the list away and Cambia takes
 * the choice away, so each moves the focus on: to Cambia, and back to the
 * search.
 */
export function CustomerPicker({
  chosen,
  onChoose,
}: {
  chosen: Chosen | null
  onChoose: (customer: Chosen | null) => void
}) {
  const { locale, messages: m } = useLocale()
  const [text, setText] = useState('')
  const [query, setQuery] = useState('')
  const [short, setShort] = useState(false)
  const field = useRef<HTMLInputElement>(null)
  const chosenLine = useRef<HTMLDivElement>(null)
  const moved = useRef<'chose' | 'changed' | null>(null)
  useEffect(() => {
    if (moved.current === 'chose' && chosen) chosenLine.current?.querySelector('button')?.focus()
    if (moved.current === 'changed' && !chosen) field.current?.focus()
    moved.current = null
  }, [chosen])
  const found = useQuery({
    queryKey: ['customers', { q: query, page: 1 }],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/customers', {
            params: { query: { q: query, page: 1 } },
          }),
        ),
      ),
    enabled: query.length >= 2,
  })
  const problem = problemOf(found.error)

  function search() {
    const asked = text.trim()
    setShort(asked.length < 2)
    if (asked.length >= 2) setQuery(asked)
  }

  if (chosen) {
    return (
      <div ref={chosenLine} className="flex flex-wrap items-center gap-3">
        <p className="font-bold">{format(m.newBooking.chosen, { name: chosen.name })}</p>
        <Button
          variant="outline"
          className="rounded-sm"
          onClick={() => {
            moved.current = 'changed'
            onChoose(null)
          }}
        >
          {m.newBooking.change}
        </Button>
      </div>
    )
  }
  const items = found.data?.items ?? []
  // Said once an answer is in, as a list's count is; and why a search was not made.
  const said = short
    ? m.newBooking.searchTooShort
    : found.data && !found.isFetching
      ? items.length === 0
        ? m.newBooking.noCustomerFound
        : plural(locale, m.customers.found, items.length)
      : ''
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <SearchField
          label={m.newBooking.searchCustomer}
          hint={m.newBooking.searchCustomerHint}
          value={text}
          onChange={setText}
          onEnter={search}
          inputRef={field}
        />
        <Button variant="outline" className="rounded-sm" onClick={search}>
          {m.bookings.submit}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p role="status" className="font-medium">
          {said}
        </p>
        {found.isFetching ? <Fetching /> : null}
      </div>
      <ProblemNotice problem={problem} />
      {items.length > 0 ? (
        <ul className="flex flex-col" aria-busy={found.isFetching || undefined}>
          {items.map((row: CustomerRow) => (
            <li key={row.id} className={ROW}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="font-bold">{customerName(row)}</span>
                {/* S8: a record whose owner asked to be erased is never chosen: no dead button, the reason. */}
                {row.erasureRequestedOn ? (
                  <span className="ms-auto">
                    <StatusChip tone="warning">{m.customer.erasureRequested}</StatusChip>
                  </span>
                ) : (
                  <Button
                    variant="outline"
                    className="ms-auto rounded-sm"
                    aria-label={`${m.newBooking.choose}: ${customerName(row)}`}
                    onClick={() => {
                      moved.current = 'chose'
                      onChoose({
                        id: row.id,
                        name: customerName(row),
                        reachable: Boolean(row.email || row.phone),
                      })
                    }}
                  >
                    {m.newBooking.choose}
                  </Button>
                )}
              </div>
              {row.erasureRequestedOn ? (
                <p className="text-sm">
                  {format(m.newBooking.erasureRequested, {
                    date: formatDate(locale, row.erasureRequestedOn, 'short'),
                  })}
                </p>
              ) : null}
              {row.phone || row.email ? (
                <p className="text-sm text-text-muted wrap-anywhere">
                  {[row.phone, row.email].filter(Boolean).join(' · ')}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
